/**
 * Common API types that can be reused across different API services
 */

// ============================================================================
// AWS Region Types and Constants
// ============================================================================

/**
 * AWS Region type - supported regions for AWS operations
 */
export type AwsRegion = 'us-east-1' | 'us-west-2';

/**
 * AWS Region constants for AWS operations
 */
export const AWS_REGIONS = {
  US_EAST_1: 'us-east-1',
  US_WEST_2: 'us-west-2',
} as const;

/**
 * Default AWS region for AWS operations
 */
export const DEFAULT_AWS_REGION: AwsRegion = AWS_REGIONS.US_EAST_1;

/**
 * AWS Region options for form components (dropdown/select)
 */
export const AWS_REGION_OPTIONS: { value: AwsRegion; label: string }[] = [
  { value: AWS_REGIONS.US_EAST_1, label: 'US East (N. Virginia) - us-east-1' },
  { value: AWS_REGIONS.US_WEST_2, label: 'US West (Oregon) - us-west-2' },
];

// ============================================================================
// Common API Response Types
// ============================================================================

/**
 * Common metadata that comes with most API responses
 */
export interface ItemMetadata {
  is_active: boolean;
  created_by: string;
  created_at: string; // UTC ISO Format
  updated_by: string;
  updated_at: string; // UTC ISO Format
}

export interface EntityDetails {
  item_metadata: ItemMetadata;
}

/**
 * Pagination information for list responses
 */
export interface Pagination {
  current_page: number;
  has_next: boolean;
  has_previous: boolean;
  page_size: number;
  total_count: number;
  total_pages: number;
}

// ============================================================================
// Form Field Metadata Types
// ============================================================================

/**
 * Form field option - represents a selectable option for a form field
 * Used for dropdowns, selects, and other option-based inputs
 */
export interface FormFieldOption {
  id: number;
  value: string;
}

/**
 * Form field - represents a single form field with its metadata
 * Contains field name, label, and available options
 */
export interface FormField {
  fieldName: string;
  label: string;
  options: FormFieldOption[];
}

/**
 * Form field metadata response - response structure from listFormFieldMetadata query
 * Contains an array of form fields with their metadata
 */
export interface FormFieldMetadataResponse {
  fields: FormField[];
}

// ============================================================================
// Cost Center Types
// ============================================================================

/**
 * Cost Center - represents a cost center with code and name
 * Used for cost center dropdown options
 */
export interface CostCenter {
  cost_center_code: string;
  cost_center_name: string;
}

// ============================================================================
// Country Types
// ============================================================================

/**
 * Country - represents a country with name and code
 * Used for country dropdown options and filters
 */
export interface Country {
  country: string;
  country_code: string;
}

/**
 * PRODUCT_TYPE classification for a single FPN, returned by listFpnProductTypes.
 * An FPN may appear more than once if it has multiple PRODUCT_TYPE rows; callers should
 * aggregate by fpn_id into a set of product types.
 */
export interface FpnProductType {
  fpn_id: number;
  product_type: string | null;
}
